import React, { memo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Input, Button, Alert } from 'antd';
import { Formik, Field, Form } from 'formik';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import * as yup from 'yup';

import FormItem from 'components/Form/StyledFormItem';
import {
  validateResetPassword,
  validateFirstTimeChangepassword,
  initChangePassword,
  resetPassword
} from 'containers/App/actions';
import {
  makeSelectIsValidPassword,
  makeSelectError,
  makeSelectInitPasswordChanged,
  makeSelectResetPassword,
  makeSelectInitChangevalidToken,
  makeSelectTokenError,
  makeSelectLoading
} from 'containers/App/selectors';

import PublicPage from './components/PublicPage';

const Content = styled.section`
  & input {
    padding: 0px;
  }
`;

const Padlock = styled.p`
  padding: 20px 0;
  display: flex;
  font-size: 14px;

  & a {
    color: #9b9b9b;
  }
`;

const validationSchema = yup.object().shape({
  password: yup
    .string()
    // character requirements are now validated on the backend
    // .min(8, 'Password is too short (minimum is 8 characters)')
    .required('Please input your Password!'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('password'), null], 'The passwords that you entered are inconsistent!')
    .required('Please input your Password!')
});

interface ResetPasswordFormProps {
  isValidReset: any;
  isValidInitChangePwd: any;
  isResetPassWord: any;
  isInitPasswordChanged: any;
  resetPassword: (values: any) => void;
  changePassword: (values: any) => void;
  validateResetPassword: (params: { reset_password_token: string }) => void;
  validateInviteToken: (params: { invitation_token: string }) => void;
  error: any;
  loading: boolean;
  tokenError: any;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = props => {
  const {
    isValidReset,
    isValidInitChangePwd,
    isResetPassWord,
    isInitPasswordChanged,
    resetPassword: resetPasswordAction,
    changePassword,
    validateResetPassword: validateResetPasswordAction,
    validateInviteToken,
    error,
    loading,
    tokenError
  } = props;

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.search.includes('reset_password_token')) {
      try {
        let token = location.search.replace('?', '').split('=')[1];
        validateResetPasswordAction({ reset_password_token: token });
      } catch (e) {
        // Handle error
      }
    } else if (location.search.includes('invitation_token')) {
      try {
        let token = location.search.replace('?', '').split('=')[1];
        validateInviteToken({ invitation_token: token });
      } catch (e) {
        // Handle error
      }
    } else {
      navigate('set-password');
    }
  }, [location.search, validateResetPasswordAction, validateInviteToken, navigate]);

  useEffect(() => {
    if (isResetPassWord) {
      if (!('status' in isResetPassWord)) {
        navigate('/signin');
      }
    } else if (isInitPasswordChanged) {
      if (!('status' in isInitPasswordChanged)) {
        navigate('/signin');
      }
    }
  }, [isResetPassWord, isInitPasswordChanged, navigate]);

  const handleSubmit = (values, bag) => {
    if (isValidReset) {
      values['reset_password_token'] = location.search.replace('?', '').split('=')[1];
      resetPasswordAction(values);
    } else if (isValidInitChangePwd) {
      values['invitation_token'] = location.search.replace('?', '').split('=')[1];
      changePassword(values);
    }
  };

  return (
    <Formik
      initialValues={{
        password: '',
        confirm_password: ''
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {() => (
        <Content>
          {!tokenError ? (
            <Form className="login-form">
              {!!error && (
                <Alert
                  style={{ marginBottom: '0.5rem' }}
                  message={error?.data?.message}
                  type={'error'}
                  showIcon
                  closable
                />
              )}
              <Field name="password">
                {({ field, meta }) => (
                  <FormItem
                    label="Password"
                    validateStatus={meta.error && meta.touched ? 'error' : null}
                    hasFeedback={meta.touched && !meta.error}
                    help={meta.error && meta.touched ? <>{meta.error}</> : null}
                    required
                  >
                    <Input.Password
                      allowClear
                      id="password"
                      label="Password"
                      placeholder="Please enter a new password"
                      {...field}
                    />
                  </FormItem>
                )}
              </Field>
              <Field name="confirm_password">
                {({ field, meta }) => (
                  <FormItem
                    label="Confirm Password"
                    validateStatus={meta.error && meta.touched ? 'error' : null}
                    hasFeedback={meta.touched && !meta.error}
                    help={meta.error && meta.touched ? <>{meta.error}</> : null}
                    required
                  >
                    <Input.Password
                      allowClear
                      id="confirm_password"
                      label="Confirm Password"
                      placeholder="Please repeat password"
                      {...field}
                    />
                  </FormItem>
                )}
              </Field>
              <Button
                style={{ width: '100%', margin: 0, marginTop: '1.5rem' }}
                type="primary"
                htmlType="submit"
                size="large"
                disabled={loading}
              >
                {isValidReset && !('status' in isValidReset) && `Reset Password`}
                {isValidInitChangePwd && !('status' in isValidInitChangePwd) && `Set Password`}
              </Button>
            </Form>
          ) : (
            error &&
            'status' in error && <Alert message={error?.data?.message} type={'error'} showIcon />
          )}
          <Padlock>
            <Link to="/signin">Back to Login</Link>
          </Padlock>
        </Content>
      )}
    </Formik>
  );
};

const mapDispatchToProps = dispatch => ({
  resetPassword: reset => dispatch(resetPassword(reset)),
  validateResetPassword: token => dispatch(validateResetPassword(token)),
  validateInviteToken: token => dispatch(validateFirstTimeChangepassword(token)),
  changePassword: token => dispatch(initChangePassword(token))
});

const selectIsValid = state => makeSelectIsValidPassword(state);
const selectResetPassword = state => makeSelectResetPassword(state);
const selectInitValueValid = state => makeSelectInitChangevalidToken(state);
const selectInitPasswordChanged = state => makeSelectInitPasswordChanged(state);
const selectError = state => makeSelectError()(state);
const selectTokenError = state => makeSelectTokenError()(state);
const selectLoading = state => makeSelectLoading()(state);
const mapStateToProps = createStructuredSelector({
  isValidReset: selectIsValid,
  isResetPassWord: selectResetPassword,
  isValidInitChangePwd: selectInitValueValid,
  isInitPasswordChanged: selectInitPasswordChanged,
  error: selectError,
  tokenError: selectTokenError,
  loading: selectLoading
});

const ResetPassword = memo((props: ResetPasswordFormProps) => {
  const { isValidReset, isValidInitChangePwd } = props;
  const title = (
    <div>
      {isValidReset && !('status' in isValidReset) && `Change Password`}

      {isValidInitChangePwd && !('status' in isValidInitChangePwd) && `Set a Password`}
    </div>
  );
  return <PublicPage title={title} form={<ResetPasswordForm {...props} />} />;
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
